import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, EmptyState, SegmentedControl, Toolbar } from "@components/ui";
import { CallDispositionModal } from "@components/CallDispositionModal";
import { CallOverlay } from "@components/CallOverlay";
import {
  PhoneIcon, SearchIcon, PlusIcon, ArrowDownLeftIcon, ArrowUpRightIcon, AlertIcon, RefreshIcon,
} from "@icons";
import type { Call } from "@aria/shared";
import { useVoice } from "@lib/voiceContext";
import { useDialer } from "@lib/dialerContext";
import { fmtNumber, toE164 } from "@lib/phone";
import { useAuth } from "@hooks/useAuth";
import { useIsMobile } from "@hooks/useIsMobile";
import { useHistorySearch } from "@hooks/useHistorySearch";
import { usePhoneLookups, isLikelySpamLine } from "@hooks/usePhoneLookups";
import { apiClient } from "@lib/apiClient";
import shell from "./DashboardPage.module.css";
import styles from "./CallsPage.module.css";

const PAGE_SIZE = 40;

// The calls list returns a bare array on the initial load and, once `before` is
// supplied, a keyset envelope ({ items, nextCursor }). Normalize both shapes.
type CallsEnvelope = { items: Call[]; nextCursor: string | null };
function asCallsPage(res: Call[] | CallsEnvelope): CallsEnvelope {
  return Array.isArray(res)
    ? { items: res, nextCursor: null }
    : { items: res.items ?? [], nextCursor: res.nextCursor ?? null };
}

const KEYPAD: { d: string; sub: string }[] = [
  { d: "1", sub: "" }, { d: "2", sub: "ABC" }, { d: "3", sub: "DEF" },
  { d: "4", sub: "GHI" }, { d: "5", sub: "JKL" }, { d: "6", sub: "MNO" },
  { d: "7", sub: "PQRS" }, { d: "8", sub: "TUV" }, { d: "9", sub: "WXYZ" },
  { d: "*", sub: "" }, { d: "0", sub: "+" }, { d: "#", sub: "" },
];

const AVATARS: { bg: string; fg: string }[] = [
  { bg: "#dbe9fb", fg: "#0066cc" },
  { bg: "#e4e4ea", fg: "#636366" },
  { bg: "#d6f2dd", fg: "#34c759" },
  { bg: "#ffe9cc", fg: "#ff9500" },
  { bg: "#f4ecff", fg: "#7a3ff2" },
];

type Kind = "in" | "out" | "missed" | "vm";

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function pickAvatar(seed: string): { bg: string; fg: string } {
  return AVATARS[hash(seed) % AVATARS.length] ?? { bg: "#e4e4ea", fg: "#636366" };
}

function initialsOf(s: string): string {
  const letters = s.replace(/[^A-Za-z]/g, "");
  if (letters.length >= 2) return letters.slice(0, 2).toUpperCase();
  const digits = s.replace(/\D/g, "");
  if (digits.length >= 2) return digits.slice(-2);
  return (s.trim()[0] ?? "?").toUpperCase();
}

function kindOf(call: Call): Kind {
  const missed = ["NO_ANSWER", "BUSY", "FAILED", "CANCELED"].includes(call.status);
  if (call.transcription) return "vm";
  if (missed) return "missed";
  return call.direction === "INBOUND" ? "in" : "out";
}

const KIND_META: Record<Kind, { label: string; color: string; nameColor: string }> = {
  in: { label: "Incoming", color: "var(--text-tertiary)", nameColor: "var(--text-primary)" },
  out: { label: "Outgoing", color: "var(--text-tertiary)", nameColor: "var(--text-primary)" },
  missed: { label: "Missed", color: "var(--color-red)", nameColor: "var(--color-red)" },
  vm: { label: "Voicemail", color: "var(--color-orange)", nameColor: "var(--text-primary)" },
};

function fmtTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }).toLowerCase();
}

function fmtDur(sec?: number): string {
  const s = sec ?? 0;
  if (!s) return "—";
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;
}

function dayBucket(iso: string): "Today" | "Yesterday" | "Earlier" {
  const d = new Date(iso);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const t = d.getTime();
  if (t >= startOfToday) return "Today";
  if (t >= startOfToday - 86400000) return "Yesterday";
  return "Earlier";
}

type CallFilter = "all" | "missed" | "vm";

export function CallsPage() {
  const voice = useVoice();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const { openDialer } = useDialer();
  const isSuper = user?.role === "SUPERADMIN";
  const [filter, setFilter] = useState<CallFilter>("all");
  const [calls, setCalls] = useState<Call[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [dialValue, setDialValue] = useState("");
  const [myNumber, setMyNumber] = useState<string>("");
  const [pendingDisposition, setPendingDisposition] = useState<Call | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [moreError, setMoreError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Initial load stays a bare array (no cursor) for backward-compat.
      const list = await apiClient.get<Call[]>(`/history/calls?limit=${PAGE_SIZE}`);
      setCalls(list);
      // A full page implies there may be older calls to fetch via the cursor.
      setHasMore(list.length >= PAGE_SIZE);
      setMoreError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't load your calls.");
    } finally {
      setLoading(false);
    }
  }, []);

  // "Load older": keyset-paginate from the oldest loaded call's startedAt.
  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    setMoreError(null);
    try {
      // Oldest loaded row is the cursor. ISO-8601 strings compare lexicographically.
      const oldest = calls.reduce<string | null>(
        (min, c) => (min === null || c.startedAt < min ? c.startedAt : min),
        null
      );
      if (!oldest) {
        setHasMore(false);
        return;
      }
      const res = await apiClient.get<Call[] | CallsEnvelope>(
        `/history/calls?limit=${PAGE_SIZE}&before=${encodeURIComponent(oldest)}`
      );
      const { items, nextCursor } = asCallsPage(res);
      setCalls((prev) => {
        const seen = new Set(prev.map((c) => c.id));
        const merged = prev.slice();
        for (const it of items) if (!seen.has(it.id)) merged.push(it);
        return merged;
      });
      setHasMore(nextCursor !== null);
    } catch (e) {
      setMoreError(e instanceof Error ? e.message : "Couldn't load older calls.");
    } finally {
      setLoadingMore(false);
    }
  }, [calls]);

  useEffect(() => {
    refresh();
    // The number outbound calls actually show — the user's assigned DID, or the
    // account fallback if none is assigned yet.
    apiClient
      .get<{ callerId?: string | null }>("/voice/caller-id")
      .then((s) => setMyNumber(s.callerId ?? ""))
      .catch(() => {});
  }, [refresh]);

  // Realtime-ish inbound refresh: the SW bridge re-dispatches push events as
  // window "aria:push" — pick up new calls/voicemails without a manual reload.
  useEffect(() => {
    const onPush = (): void => {
      void refresh();
    };
    window.addEventListener("aria:push", onPush);
    return () => window.removeEventListener("aria:push", onPush);
  }, [refresh]);

  const party = (c: Call) => (c.direction === "INBOUND" ? c.from : c.to);

  // Cached caller-ID (Twilio Lookup) for every number in the loaded page.
  const lookupPhones = useMemo(() => calls.map(party), [calls]);
  const lookups = usePhoneLookups(lookupPhones);

  // Server-side search augmentation (>= 2 chars, debounced; null on error).
  const serverResults = useHistorySearch(query);

  const kindFiltered = useMemo(() => {
    if (filter === "all") return calls;
    return calls.filter((c) => (filter === "vm" ? kindOf(c) === "vm" : kindOf(c) === "missed"));
  }, [calls, filter]);

  const filtered = useMemo(() => {
    const calls = kindFiltered;
    const q = query.trim().toLowerCase();
    if (!q) return calls;
    // Instant first pass: client-side filter over the loaded page…
    const matched = calls.filter((c) => party(c).toLowerCase().includes(q));
    // …then union in server matches (searches from/to/transcription across the
    // whole history), deduped by id. Sorted newest-first so day groups stay stable.
    const serverCalls = serverResults?.calls ?? [];
    if (serverCalls.length === 0) return matched;
    const seen = new Set(matched.map((c) => c.id));
    const merged = matched.slice();
    for (const c of serverCalls) {
      if (!seen.has(c.id)) {
        seen.add(c.id);
        merged.push(c);
      }
    }
    return merged.sort((a, b) => b.startedAt.localeCompare(a.startedAt));
  }, [kindFiltered, query, serverResults]);

  const groups = useMemo(() => {
    const buckets: Record<"Today" | "Yesterday" | "Earlier", Call[]> = {
      Today: [], Yesterday: [], Earlier: [],
    };
    for (const c of filtered) buckets[dayBucket(c.startedAt)].push(c);
    return (["Today", "Yesterday", "Earlier"] as const)
      .map((k) => ({ label: k, items: buckets[k] }))
      .filter((g) => g.items.length > 0);
  }, [filtered]);

  const todayCount = calls.filter((c) => dayBucket(c.startedAt) === "Today").length;

  const callBack = (to: string) => {
    const num = toE164(to, Boolean(isSuper));
    if (num) void voice.dial(num);
  };
  const pushDigit = (d: string) => setDialValue((v) => (v.length < 24 ? v + d : v));
  const backspace = () => setDialValue((v) => v.slice(0, -1));
  // International "+" is superadmin-only; everyone else defaults to US (+1).
  const pushPlus = () => setDialValue((v) => (isSuper && !v.includes("+") ? "+" + v : v));
  const dialNow = () => {
    const num = toE164(dialValue, Boolean(isSuper));
    if (num) {
      void voice.dial(num);
    }
  };

  const busy = voice.status === "in-call" || voice.status === "connecting";

  const dialerPanel = (
    <div className={styles.dialer}>
      <div className={styles.dialerFrom}>
        {myNumber ? `Calling from ${fmtNumber(myNumber)}` : "Dialer"}
      </div>
      <div className={`${styles.dialDisplay} ${dialValue ? styles.dialActive : ""}`}>
        {dialValue ? fmtNumber(dialValue) : "Dial a number"}
      </div>
      <div className={styles.keypad}>
        {KEYPAD.map((k) => (
          <button key={k.d} className={styles.key} onClick={() => pushDigit(k.d)} disabled={busy}>
            <span className={styles.keyDigit}>{k.d}</span>
            {k.sub && <span className={styles.keySub}>{k.sub}</span>}
          </button>
        ))}
      </div>
      <div className={styles.dialControls}>
        {isSuper ? (
          <button
            className={styles.dialCtrl}
            onClick={pushPlus}
            disabled={busy}
            aria-label="Insert plus for international dialing"
            title="International (+)"
          >
            +
          </button>
        ) : (
          <span className={styles.dialCtrlSpacer} aria-hidden />
        )}
        <button className={styles.dialCall} onClick={dialNow} disabled={busy || !dialValue} aria-label="Call">
          <PhoneIcon width={26} height={26} />
        </button>
        <button
          className={styles.dialCtrl}
          onClick={backspace}
          disabled={busy || !dialValue}
          aria-label="Backspace"
          title="Backspace"
        >
          ⌫
        </button>
      </div>
    </div>
  );

  return (
    <>
      <Toolbar
        large
        title="Calls"
        subtitle={loading ? undefined : `${todayCount} today`}
        search={
          <label className={styles.search}>
            <SearchIcon width={16} height={16} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search"
              aria-label="Search calls"
            />
          </label>
        }
        trailing={
          <Button
            size="sm"
            leftIcon={<PlusIcon width={16} height={16} />}
            onClick={() => {
              setDialValue("");
              if (isMobile) openDialer();
            }}
          >
            New call
          </Button>
        }
      />
      <div className={shell.scroll}>
        <div className={styles.layout}>
          <div className={styles.historyCol}>
            <SegmentedControl<CallFilter>
              value={filter}
              onChange={setFilter}
              options={[
                { value: "all", label: "All" },
                { value: "missed", label: "Missed" },
                { value: "vm", label: "Voicemail" },
              ]}
            />
            {loading && !calls.length ? (
              <div className={shell.skeletonRow} />
            ) : error && !calls.length ? (
              <EmptyState
                icon={<span style={{ color: "var(--color-red)", display: "inline-flex" }}><AlertIcon /></span>}
                title="Couldn't load calls"
                description={error}
                action={
                  <Button leftIcon={<RefreshIcon width={16} height={16} />} onClick={() => void refresh()}>
                    Retry
                  </Button>
                }
              />
            ) : groups.length === 0 ? (
              <EmptyState icon={<PhoneIcon />} title="No calls yet" description="Place a call to see history." />
            ) : (
              groups.map((g) => (
                <section key={g.label} className={styles.group}>
                  <div className={styles.groupLabel}>{g.label}</div>
                  <div className={styles.card}>
                    {g.items.map((c) => {
                      const k = kindOf(c);
                      const meta = KIND_META[k];
                      const av = pickAvatar(party(c));
                      const info = lookups[party(c)];
                      const name = info?.callerName ?? fmtNumber(party(c));
                      const spam = isLikelySpamLine(info?.lineType);
                      return (
                        <div key={c.id} className={styles.row}>
                          <div className={styles.avatar} style={{ background: av.bg, color: av.fg }}>
                            {initialsOf(party(c))}
                          </div>
                          <div
                            className={styles.rowBody}
                            role="button"
                            tabIndex={0}
                            onClick={() => setPendingDisposition(c)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setPendingDisposition(c); }
                            }}
                          >
                            <div className={styles.name} style={{ color: meta.nameColor }}>
                              {name}
                              {spam && <span className={styles.spamChip}>Possible spam</span>}
                            </div>
                            <div className={styles.dir} style={{ color: meta.color }}>
                              {c.direction === "INBOUND" ? (
                                <ArrowDownLeftIcon width={15} height={15} />
                              ) : (
                                <ArrowUpRightIcon width={15} height={15} />
                              )}
                              <span>{meta.label}</span>
                              {info?.callerName && <span className={styles.lookupNumber}>{fmtNumber(party(c))}</span>}
                            </div>
                            {c.aiSummary && (
                              <div className={styles.aiSummary}>✦ {c.aiSummary}</div>
                            )}
                            {c.transcription && (
                              <div className={styles.note}>{c.transcription}</div>
                            )}
                          </div>
                          <div className={styles.rowTime}>
                            <div className={styles.time}>{fmtTime(c.startedAt)}</div>
                            <div className={styles.dur}>{fmtDur(c.durationSec)}</div>
                          </div>
                          <button
                            className={styles.callBtn}
                            onClick={() => callBack(party(c))}
                            disabled={busy}
                            aria-label={`Call ${name}`}
                          >
                            <PhoneIcon width={18} height={18} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))
            )}
            {!loading && !error && groups.length > 0 && (hasMore || moreError) && (
              <div className={styles.loadMore}>
                {moreError && <span className={styles.loadMoreErr}>{moreError}</span>}
                <Button variant="gray" size="sm" isLoading={loadingMore} onClick={() => void loadMore()}>
                  {moreError ? "Try again" : "Load older"}
                </Button>
              </div>
            )}
          </div>

          {!isMobile && <aside className={styles.dialerCol}>{dialerPanel}</aside>}
        </div>
      </div>

      <CallOverlay voice={voice} />
      <CallDispositionModal
        call={pendingDisposition}
        onClose={() => setPendingDisposition(null)}
        onSaved={() => { void refresh(); }}
      />
    </>
  );
}
